import React from "react";

import Icon from "./Icon";
import ZestComponent from "./ZestComponent";
import type { TimelineStatus } from "./types";

// React rebuild of the ZEST v2 Figma component set Zest::TimelineIcon

const STATUS_CLASSES: Record<Exclude<TimelineStatus, "">, string> = {
  success: "bg-success text-negative",
  warning: "bg-warning text-negative",
  danger: "bg-danger text-negative",
  white: "bg-negative text-brand",
};

const STATUS_ICONS: Partial<Record<Exclude<TimelineStatus, "">, string>> = {
  success: "check",
  warning: "loader",
  danger: "xmark",
};

const DEFAULT_STATUS_CLASSES = "bg-light text-primary";
const DEFAULT_ICON = "clipboard-list-check";

// Props arriving from `react_component` aren't type-checked at runtime, so an
// unrecognised status (a typo in a Rails view) has to fall back rather than
// interpolate `undefined` into the class list.
const classesForStatus = (status: TimelineStatus): string =>
  (status && STATUS_CLASSES[status]) || DEFAULT_STATUS_CLASSES;

const iconForStatus = (status: TimelineStatus, icon: string | null): string =>
  icon ?? (status ? STATUS_ICONS[status] ?? DEFAULT_ICON : DEFAULT_ICON);

export interface TimelineIconProps {
  status?: TimelineStatus;
  icon?: string | null;
  iconText?: string | null;
  primaryText?: string;
  secondaryText?: string;
}

const TimelineIcon = ({
  status = "",
  icon = null,
  iconText = null,
  primaryText = "",
  secondaryText = "",
}: TimelineIconProps): JSX.Element => {
  const badgeClasses = classesForStatus(status);
  const resolvedIcon = iconForStatus(status, icon);

  return (
    <ZestComponent
      name="timeline-icon"
      className="flex-1 w-full flex flex-col items-center gap-1 text-center whitespace-normal min-w-[220px]"
      variants={{ status }}
    >
      <div
        className={`flex items-center justify-center rounded h-6 min-w-[1.5rem] text-sm ${
          iconText ? "px-2" : ""
        } ${badgeClasses}`}
      >
        {iconText || <Icon icon={resolvedIcon} size="sm" />}
      </div>
      {/* Rendered as HTML because callers pass small bits of markup for
          emphasis (e.g. "<b>Promise Date</b>"), the same way the ERB Zest
          component did. Both values must therefore be author-controlled —
          never interpolate user input into them without escaping first. */}
      {primaryText && (
        <div className="text-sm text-primary font-bold" dangerouslySetInnerHTML={{ __html: primaryText }} />
      )}
      {secondaryText && (
        <div className="text-sm text-secondary font-medium" dangerouslySetInnerHTML={{ __html: secondaryText }} />
      )}
    </ZestComponent>
  );
};

export default TimelineIcon;
